<template>
  <span>
    <button class="btn btn-success"
            @click="showCreateProjectModal"
            qid="create-p-button"
    >
      <font-awesome-icon icon="plus" class="mr-2"/>
      {{ $t('projects.create_button') }}
    </button>

    <!-- Start project: pick a goal (or a recurring project to run again), then set it up (US 9396) -->
    <b-modal ref="project-launcher"
             @hidden="resetLauncher"
             modal-class="project-launcher-modal"
             :size="step === 'run' ? 'lg' : 'xl'"
             centered
             hide-header
             hide-footer
    >
      <div class="project-launcher">

        <!-- Step 1: launcher -->
        <template v-if="step === 'launcher'">
          <div class="project-launcher__head">
            <div class="grow">
              <h2 qid="create-p-title">{{ $t('projects.launcher.title') }}</h2>
              <div class="project-launcher__sub">
                {{ $t('projects.launcher.subtitle') }}
                <span v-if="type && !showAllGoals && hasMoreGoals"
                      class="project-launcher__link ml-1"
                      @click="showAllGoals = true"
                      qid="create-p-all-goals"
                >{{ $t('projects.launcher.show_all_goals') }}</span>
              </div>
            </div>
            <span class="project-launcher__close" @click="hideLauncher" qid="create-p-close">
              <font-awesome-icon icon="times"/>
            </span>
          </div>

          <div class="project-launcher__body">
            <div class="project-launcher__search">
              <font-awesome-icon icon="search"/>
              <input type="text"
                     v-model="search"
                     :placeholder="$t('projects.launcher.search_placeholder')"
                     qid="create-p-search"
              >
            </div>

            <template v-if="filteredRunItems.length">
              <div class="project-launcher__section">
                <h3>{{ $t('projects.launcher.run_again') }}</h3>
                <span class="project-launcher__section-sub">{{ $t('projects.launcher.run_again_sub') }}</span>
                <span v-if="filteredRunItems.length > 3"
                      class="project-launcher__link ml-auto"
                      @click="showAllRuns = !showAllRuns"
                      qid="create-p-see-all-runs"
                >
                  {{ showAllRuns ? $t('projects.launcher.show_less') : $t('projects.launcher.see_all', {count: filteredRunItems.length}) }}
                </span>
              </div>
              <div class="project-launcher__runs" :class="{'project-launcher__runs--all': showAllRuns}">
                <div class="project-launcher__run"
                     v-for="item in visibleRunItems"
                     :key="item.slug"
                     @click="openRun(item)"
                     qid="create-p-run-item"
                >
                  <span class="project-launcher__icon project-launcher__icon--sm" :class="'project-launcher__icon--' + goalColor(item.type)">
                    <font-awesome-icon :icon="goalIcon(item.type)"/>
                  </span>
                  <div class="grow">
                    <div class="project-launcher__run-name">{{ item.title }}</div>
                    <div class="project-launcher__run-meta" :class="{'project-launcher__run-meta--due': item.run_due}">{{ runMeta(item) }}</div>
                  </div>
                  <span class="btn btn-secondary btn-sm">
                    <font-awesome-icon icon="play" class="mr-1"/>{{ $t('projects.launcher.run') }}
                  </span>
                </div>
              </div>
            </template>

            <div class="project-launcher__section">
              <h3>{{ $t('projects.launcher.start_new') }}</h3>
            </div>
            <div class="project-launcher__goals" v-if="filteredGoals.length">
              <div class="project-launcher__goal"
                   v-for="goal in filteredGoals"
                   :key="goal.key"
                   :class="{'project-launcher__goal--hot': goal.key === highlightedGoal}"
                   @click="selectGoal(goal)"
                   :qid="'create-p-goal-' + goal.key"
              >
                <span class="project-launcher__icon" :class="'project-launcher__icon--' + goal.color">
                  <font-awesome-icon :icon="goal.icon"/>
                </span>
                <div class="project-launcher__goal-name">{{ $t('projects.launcher.goals.' + goal.key + '.title') }}</div>
                <div class="project-launcher__goal-text">{{ $t('projects.launcher.goals.' + goal.key + '.description') }}</div>
                <div class="project-launcher__chips">
                  <span v-for="chip in goalChips(goal)"
                        :key="chip"
                        class="project-launcher__chip"
                        :class="'project-launcher__chip--' + goal.color">{{ chip }}</span>
                </div>
              </div>
            </div>

            <template v-if="search && filteredTemplates.length">
              <div class="project-launcher__section">
                <h3>{{ $t('projects.launcher.templates') }}</h3>
              </div>
              <div class="project-launcher__template-results">
                <div class="project-launcher__run"
                     v-for="template in filteredTemplates"
                     :key="template.id"
                     @click="selectTemplateResult(template)"
                     qid="create-p-template-result"
                >
                  <span class="project-launcher__icon project-launcher__icon--sm" :class="'project-launcher__icon--' + goalByTemplateType(template.type).color">
                    <font-awesome-icon :icon="goalByTemplateType(template.type).icon"/>
                  </span>
                  <div class="grow">
                    <div class="project-launcher__run-name">{{ template.name }}</div>
                    <div class="project-launcher__run-meta">{{ $t('projects.launcher.goals.' + goalByTemplateType(template.type).key + '.title') }}</div>
                  </div>
                  <font-awesome-icon icon="chevron-right" class="text-muted"/>
                </div>
              </div>
            </template>

            <div v-if="!filteredGoals.length && !(search && filteredTemplates.length) && !filteredRunItems.length" class="text-muted">
              {{ $t('projects.launcher.no_results') }}
            </div>
          </div>

          <div class="project-launcher__foot">
            <span class="grow"></span>
            <b-button variant="secondary" @click="hideLauncher" qid="create-p-cancel-button">
              {{ $t('projects.create.cancel') }}
            </b-button>
          </div>
        </template>

        <!-- Step 2: set up the chosen goal -->
        <b-form v-if="step === 'setup' && goal" @submit="formSubmit" qid="create-p-form">
          <div class="project-launcher__head">
            <span class="project-launcher__back"
                  v-if="canGoBack"
                  @click="backToLauncher"
                  qid="create-p-back">
              <font-awesome-icon icon="chevron-left"/>
            </span>
            <span class="project-launcher__icon" :class="'project-launcher__icon--' + goal.color">
              <font-awesome-icon :icon="goal.icon"/>
            </span>
            <div class="grow">
              <h2>{{ $t('projects.launcher.goals.' + goal.key + '.title') }}</h2>
              <div class="project-launcher__sub">{{ $t('projects.launcher.goals.' + goal.key + '.setup_text') }}</div>
            </div>
            <span class="project-launcher__close" @click="hideLauncher">
              <font-awesome-icon icon="times"/>
            </span>
          </div>

          <div class="project-launcher__split">
            <div class="project-launcher__form">
              <b-alert v-if="error.length"
                       show
                       variant="danger"
                       qid="create-p-error">{{ error }}</b-alert>

              <!-- Template -->
              <div class="project-launcher__field" v-if="goal.templateType">
                <label>{{ goal.key === 'audit' ? $t('projects.launcher.audit_program_label') : $t('projects.create.assessment_label') }}</label>
                <div v-if="!goalTemplates.length" class="text-muted">{{ $t('projects.launcher.no_templates') }}</div>
                <div class="project-launcher__templates" v-if="goalTemplates.length && (goalTemplates.length <= 6 || showAllTemplates)">
                  <div class="project-launcher__template"
                       v-for="template in goalTemplates"
                       :key="template.id"
                       :class="{'project-launcher__template--selected': projectAssessment && projectAssessment.id === template.id}"
                       @click="selectTemplate(template)"
                       qid="create-p-form-a"
                  >
                    <span class="project-launcher__radio"></span>
                    <div class="project-launcher__template-name">{{ template.name }}</div>
                    <div class="project-launcher__template-text" v-if="template.description">{{ template.description | truncate(90) }}</div>
                  </div>
                </div>
                <multiselect
                    v-if="goalTemplates.length > 6 && !showAllTemplates"
                    v-model="projectAssessment"
                    :options="goalTemplates"
                    :placeholder="$t('projects.create.assessment_placeholder')"
                    label="name"
                    track-by="id"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    @select="selectTemplate"
                    qid="create-p-form-a"
                ></multiselect>
              </div>

              <!-- Controls or security measures -->
              <div class="project-launcher__field" v-if="goal.key === 'controls'">
                <label>{{ $t('projects.launcher.control_kind_label') }}</label>
                <div class="project-launcher__seg">
                  <div :class="{'on': controlKind === 'control'}" @click="setControlKind('control')">{{ $t('projects.launcher.kind_controls') }}</div>
                  <div :class="{'on': controlKind === 'security_measure'}" @click="setControlKind('security_measure')">{{ $t('projects.launcher.kind_security_measures') }}</div>
                </div>
              </div>

              <!-- Scope -->
              <div class="project-launcher__field" v-if="needsScope">
                <label>{{ $t('projects.launcher.scope_label') }}</label>
                <div class="project-launcher__scopes">
                  <div class="project-launcher__scope"
                       v-for="option in scopeOptions"
                       :key="option.value"
                       :class="{'project-launcher__scope--selected': projectScope === option.value}"
                       @click="selectScope(option.value)"
                       qid="create-p-form-scope"
                  >
                    <span class="project-launcher__check"><font-awesome-icon icon="check" v-if="projectScope === option.value"/></span>
                    {{ $t('projects.launcher.scopes.' + option.value) }}
                    <span class="project-launcher__count" v-if="scopeCounts[option.value] !== undefined">{{ scopeCounts[option.value] }}</span>
                  </div>
                </div>
                <div class="project-launcher__help">{{ $t('projects.launcher.scope_help') }}</div>
              </div>

              <!-- Name -->
              <div class="project-launcher__field">
                <label>{{ $t('projects.launcher.name_label') }}</label>
                <div class="project-launcher__input">
                  <input type="text"
                         v-model="title"
                         maxlength="255"
                         required
                         @input="titleTouched = true"
                         :placeholder="$t('projects.create.title_placeholder')"
                         qid="create-s-form-name"
                  >
                  <span class="project-launcher__suggested" v-if="!titleTouched && title">
                    <font-awesome-icon icon="sparkles" class="mr-1"/>{{ $t('projects.launcher.name_suggested') }}
                  </span>
                </div>
              </div>

              <!-- How often -->
              <div class="project-launcher__field">
                <label>{{ $t('projects.launcher.how_often') }}</label>
                <div class="project-launcher__inline">
                  <div class="project-launcher__seg">
                    <div :class="{'on': repeats}" @click="setRepeats(true)" qid="create-p-repeats">
                      <font-awesome-icon icon="repeat" class="mr-1"/>{{ $t('projects.launcher.repeats') }}
                    </div>
                    <div :class="{'on': !repeats}" @click="setRepeats(false)" qid="create-p-once">{{ $t('projects.launcher.once') }}</div>
                  </div>
                  <div class="project-launcher__control" v-if="repeats">
                    <select v-model="scheduleOption" class="project-launcher__bare-select" qid="create-p-schedule">
                      <option v-for="option in scheduleOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                    <font-awesome-icon icon="chevron-down" class="project-launcher__control-chevron"/>
                  </div>
                  <div class="project-launcher__control" v-if="repeats && scheduleOption === 'custom'">
                    <span class="project-launcher__control-label">{{ $t('projects.launcher.every') }}</span>
                    <span class="project-launcher__stepper">
                      <span @click="stepCustomEvery(-1)" qid="create-p-custom-minus"><font-awesome-icon icon="minus"/></span>
                      <input type="number"
                             min="1"
                             v-model="customEvery"
                             qid="create-p-custom-every">
                      <span @click="stepCustomEvery(1)" qid="create-p-custom-plus"><font-awesome-icon icon="plus"/></span>
                    </span>
                    <select v-model="customInterval" class="project-launcher__bare-select" qid="create-p-custom-interval">
                      <option v-for="option in intervalOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                    <font-awesome-icon icon="chevron-down" class="project-launcher__control-chevron"/>
                  </div>
                  <div v-if="isScheduled" class="project-launcher__control project-launcher__date">
                    <font-awesome-icon icon="calendar-alt" class="project-launcher__control-icon"/>
                    <span class="project-launcher__control-label">{{ $t('projects.launcher.first_run') }}</span>
                    <datepicker
                        v-model="firstRun"
                        :format="dateFormat"
                        :monday-first="true"
                        :disabledDates="disabledDates"
                        calendar-class="project-launcher__calendar"
                        qid="create-p-first-run"
                    ></datepicker>
                  </div>
                </div>
                <div class="project-launcher__help" v-if="!repeats">{{ $t('projects.launcher.once_help') }}</div>
                <div class="project-launcher__help" v-if="repeats && !isScheduled">{{ $t('projects.launcher.repeats_unscheduled_help') }}</div>
                <div class="project-launcher__auto"
                     v-if="isScheduled"
                     @click="autoCreate = !autoCreate"
                     qid="create-p-auto-create">
                  <span class="project-launcher__switch" :class="{'on': autoCreate}"></span>
                  <div>
                    <b>{{ $t('projects.launcher.auto_create_title') }}</b>
                    <span>{{ $t('projects.launcher.auto_create_help') }}</span>
                  </div>
                </div>
              </div>

              <!-- Contributors -->
              <p v-if="isNewEngagement" class="text-muted" qid="create-s-engagement-team-hint">
                {{ $t('projects.engagement.team.create_hint') }}
              </p>
              <div class="project-launcher__field" v-if="!isNewEngagement">
                <label>{{ $t('projects.create.owners_label') }}</label>
                <multiselect
                    v-model="selectedUsers"
                    :options="users"
                    :multiple="true"
                    :taggable="false"
                    track-by="id"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    :placeholder="$t('projects.create.owners_placeholder')"
                    label="name"
                    @search-change="loadUsers"
                    qid="create-s-users-select"
                >
                  <template slot="tag" slot-scope="props">
                    <span class="mr-3 user-tag">
                      <Avatar :object="props.option" size="xs"></Avatar>
                      <span class="ml-2">{{ props.option.name }}</span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <UserDropdownOption :user="props.option"></UserDropdownOption>
                  </template>
                </multiselect>
              </div>

              <!-- More options -->
              <div class="project-launcher__more" @click="showMore = !showMore" qid="create-p-more">
                <font-awesome-icon :icon="showMore ? 'chevron-up' : 'chevron-down'" class="mr-2"/>
                {{ $t('projects.launcher.more_options') }}
                <span class="project-launcher__chip project-launcher__chip--grey ml-2" v-if="!showMore">{{ $t('projects.create.description_label') }}</span>
                <span class="project-launcher__chip project-launcher__chip--grey ml-1" v-if="!showMore">{{ $t('processes.create.tags_label') }}</span>
              </div>
              <template v-if="showMore">
                <div class="project-launcher__field">
                  <label>{{ $t('projects.create.description_label') }}</label>
                  <b-form-textarea
                      v-model="description"
                      rows="3"
                      max-rows="6"
                      :placeholder="$t('projects.create.description_placeholder')"
                      qid="create-s-form-description"
                  ></b-form-textarea>
                </div>
                <div class="project-launcher__field">
                  <label>{{ $t('processes.create.tags_label') }}</label>
                  <multiselect
                      v-model="selectedTags"
                      :options="tags"
                      :multiple="true"
                      :select-label="$t('system.dropdown_select_label')"
                      :selected-label="$t('system.dropdown_selected_label')"
                      :deselect-label="$t('system.dropdown_deselect_label')"
                      :taggable="true"
                      track-by="name_translated"
                      :placeholder="$t('processes.create.tags_placeholder')"
                      label="name_translated"
                      qid="create-do-tags-select"
                      @search-change="loadTags"
                      @tag="addTag"
                  ></multiselect>
                </div>
              </template>
            </div>

            <!-- What happens next -->
            <div class="project-launcher__preview">
              <h4>{{ $t('projects.launcher.next_title') }}</h4>
              <div class="project-launcher__preview-card">
                <div class="project-launcher__preview-name">{{ title || $t('projects.launcher.goals.' + goal.key + '.title') }}</div>
                <div class="project-launcher__chips">
                  <span class="project-launcher__chip" :class="'project-launcher__chip--' + goal.color">{{ $t('projects.launcher.goals.' + goal.key + '.title') }}</span>
                  <span class="project-launcher__chip project-launcher__chip--grey">{{ scheduleSummary }}</span>
                  <span class="project-launcher__chip project-launcher__chip--grey" v-if="needsScope && projectScope && scopeCounts[projectScope] !== undefined">
                    {{ scopeCounts[projectScope] }} {{ $t('projects.launcher.scopes.' + projectScope) }}
                  </span>
                </div>
              </div>
              <div class="project-launcher__steps">
                <div class="project-launcher__step" v-for="(item, index) in nextSteps" :key="index">
                  <span class="project-launcher__step-dot">{{ index + 1 }}</span>
                  <div><b>{{ item.title }}</b><span>{{ item.text }}</span></div>
                </div>
              </div>
              <div class="project-launcher__tip">
                <font-awesome-icon icon="info-circle" class="mr-2"/>{{ $t('projects.launcher.next_tip') }}
              </div>
            </div>
          </div>

          <div class="project-launcher__foot">
            <span class="project-launcher__progress" v-if="canGoBack">
              <span class="on"></span><span class="on"></span>{{ $t('projects.launcher.step_of', {current: 2, total: 2}) }}
            </span>
            <span class="grow"></span>
            <b-button variant="secondary" @click="hideLauncher" qid="create-p-cancel-button">
              {{ $t('projects.create.cancel') }}
            </b-button>
            <b-button type="submit"
                      class="ml-3"
                      variant="success"
                      :disabled="buttonDisabled"
                      qid="create-p-submit-button">
              <font-awesome-icon v-if="buttonDisabled"
                                 icon="spinner"
                                 size="sm"
                                 class="fa-spin mr-1"/>
              {{ $t('projects.launcher.create') }}
            </b-button>
          </div>
        </b-form>

        <!-- Run a recurring project again -->
        <b-form v-if="step === 'run' && selectedRun" @submit="runFormSubmit" qid="run-p-form">
          <div class="project-launcher__head">
            <span class="project-launcher__back"
                  v-if="!runOnly"
                  @click="backToLauncher"
                  qid="run-p-back">
              <font-awesome-icon icon="chevron-left"/>
            </span>
            <div class="grow">
              <h2 qid="run-p-title">{{ $t('projects.launcher.run_again') }}</h2>
              <div class="project-launcher__sub">{{ $t('projects.launcher.run_sub') }}</div>
            </div>
            <span class="project-launcher__close" @click="hideLauncher">
              <font-awesome-icon icon="times"/>
            </span>
          </div>

          <div class="project-launcher__body">
            <b-alert v-if="error.length"
                     show
                     variant="danger"
                     qid="run-p-error">{{ error }}</b-alert>

            <div class="project-launcher__run-head">
              <span class="project-launcher__icon" :class="'project-launcher__icon--' + goalColor(selectedRun.type)">
                <font-awesome-icon :icon="goalIcon(selectedRun.type)"/>
              </span>
              <div class="grow">
                <div class="project-launcher__run-name">{{ selectedRun.title }}</div>
                <div class="project-launcher__run-meta" :class="{'project-launcher__run-meta--due': selectedRun.run_due}">{{ runMeta(selectedRun) }}</div>
              </div>
            </div>

            <!-- The round's name: the recurring project and the day it starts, which the user can change -->
            <div class="project-launcher__field">
              <label>{{ $t('projects.launcher.name_label') }}</label>
              <div class="project-launcher__input">
                <input type="text"
                       v-model="runForm.title"
                       maxlength="255"
                       required
                       @input="runTitleTouched = true"
                       qid="run-p-title-input">
              </div>
            </div>

            <div class="project-launcher__field">
              <label>{{ $t('projects.launcher.when') }}</label>
              <div class="project-launcher__inline">
                <div class="project-launcher__control project-launcher__date">
                  <font-awesome-icon icon="calendar-alt" class="project-launcher__control-icon"/>
                  <datepicker
                      v-model="runForm.start_date"
                      :format="dateFormat"
                      :monday-first="true"
                      :disabledDates="disabledRunDates"
                      @selected="setRunDue(runDuePreset, $event)"
                      calendar-class="project-launcher__calendar"
                      qid="run-p-start-date"
                  ></datepicker>
                </div>
                <font-awesome-icon icon="arrow-right" class="text-muted"/>
                <div class="project-launcher__control project-launcher__date">
                  <font-awesome-icon icon="calendar-alt" class="project-launcher__control-icon"/>
                  <datepicker
                      v-model="runForm.due_date"
                      :format="dateFormat"
                      :monday-first="true"
                      :disabledDates="disabledRunEndDates"
                      @selected="runDuePreset = ''"
                      calendar-class="project-launcher__calendar"
                      qid="run-p-due-date"
                  ></datepicker>
                </div>
                <span class="project-launcher__quick" :class="{'on': runDuePreset === '2w'}" @click="setRunDue('2w')">{{ $t('projects.launcher.two_weeks') }}</span>
                <span class="project-launcher__quick" :class="{'on': runDuePreset === '1m'}" @click="setRunDue('1m')">{{ $t('projects.launcher.one_month') }}</span>
              </div>
            </div>

            <!-- Assessments don't send a message or attachments to assignees -->
            <template v-if="!runIsAssessment">
            <div class="project-launcher__field">
              <label>{{ $t('projects.create.message_label') }}</label>
              <b-form-textarea
                  v-model="runForm.schedule_message"
                  rows="4"
                  max-rows="6"
                  :placeholder="$t('projects.create.message_placeholder')"
                  qid="run-p-message"
              ></b-form-textarea>
            </div>

            <b-form-checkbox v-model="runForm.include_attachments"
                             :value="1"
                             :unchecked-value="0"
                             qid="run-p-include-attachments">
              {{ $t('system.include_project_attachments') }}
            </b-form-checkbox>

            <div class="project-launcher__link mt-2"
                 v-if="!showAddFiles"
                 @click="showAddFiles = true"
                 qid="run-p-add-files">
              <font-awesome-icon icon="paperclip" class="mr-1"/>{{ $t('projects.launcher.add_files') }}
            </div>
            <div v-show="showAddFiles" class="mt-2">
              <label class="text-bold">
                {{ $t('maintenance.retention_rules.create.attachments') }}
                <span class="text-muted">({{ $t('attachments.file_size_label') }})</span>
              </label>
              <p v-for="file in selectedFiles" :key="file.name" class="mb-1">{{ file.name }}</p>
              <label class="btn btn-secondary cursor-pointer" qid="run-p-attachments-button">
                <input type="file"
                       ref="runFiles"
                       multiple
                       @change="onFileChange">
                <span><font-awesome-icon icon="cloud-upload-alt"/> {{ $t('attachments.upload_button') }}</span>
              </label>
              <div class="mt-2">
                <label class="text-bold">{{ $t('maintenance.third_parties.create.links_label') }}</label>
                <p v-for="(link, index) in runForm.links" :key="index" class="mb-1">
                  <span class="float-right text-muted cursor-pointer" @click="runForm.links.splice(index, 1)">
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                  <a :href="link.url" target="_blank">{{ link.name ? link.name : link.url }}</a>
                </p>
                <div class="project-launcher__inline">
                  <b-form-input type="text"
                                v-model="inputLink"
                                :placeholder="$t('maintenance.third_parties.create.links_placeholder')"
                                qid="run-p-links"></b-form-input>
                  <b-form-input type="text"
                                v-model="inputLinkName"
                                :placeholder="inputLink ? inputLink : $t('table.name')"
                                qid="run-p-link-name"></b-form-input>
                  <span class="btn btn-link" @click="handleLink" qid="run-p-add-link">+ {{ $t('system.add_link') }}</span>
                </div>
              </div>
            </div>
            </template>
          </div>

          <div class="project-launcher__foot">
            <span class="grow"></span>
            <b-button variant="secondary" @click="hideLauncher" qid="run-p-cancel-button">
              {{ $t('projects.create.cancel') }}
            </b-button>
            <b-button type="submit"
                      class="ml-3"
                      variant="success"
                      :disabled="buttonDisabled"
                      qid="run-p-submit-button">
              <font-awesome-icon v-if="buttonDisabled"
                                 icon="spinner"
                                 size="sm"
                                 class="fa-spin mr-1"/>
              <font-awesome-icon v-else icon="play" class="mr-1"/>
              {{ $t('projects.launcher.run_now') }}
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </span>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Datepicker from 'vuejs-datepicker';
  import Avatar from '@/components/Avatar.vue';
  import UserDropdownOption from '@/components/UserDropdownOption.vue';

  // Pages that open the dialog on their own assets: the `scope` prop and the scope it starts with
  const PAGE_SCOPES = { processingActivities: 'process', systems: 'system', contracts: 'third_party' };

  // What a user can start, in their words, and the project type behind it (US 9396)
  const GOALS = [
    {key: 'audit', projectType: 'control_assessment', family: 'assessment', templateType: 'control', icon: 'clipboard-list', color: 'teal'},
    {key: 'risk', projectType: 'risk_assessment', family: 'assessment', templateType: 'risk', icon: 'exclamation-triangle', color: 'rose'},
    {key: 'bia', projectType: 'bia_assessment', family: 'assessment', templateType: 'bia', icon: 'wave-square', color: 'violet'},
    {key: 'update', projectType: 'update', family: 'update', icon: 'sync-alt', color: 'blue', chips: ['yearly_review', 'ropa']},
    {key: 'validation', projectType: 'validation', family: 'update', icon: 'check-double', color: 'blue', chips: ['validation']},
    {key: 'controls', projectType: 'control', family: 'control', icon: 'shield-alt', color: 'amber', chips: ['controls', 'security_measures']}
  ];

  // Repeat intervals of the schedule drop-down, stored as repeat_after + repeat_after_interval
  const SCHEDULES = {
    month: {every: 1, interval: 'Months'},
    quarter: {every: 3, interval: 'Months'},
    half_year: {every: 6, interval: 'Months'},
    year: {every: 1, interval: 'Years'}
  };

  export default {
    name: 'CreateProjectModal',
    data: function() {
      return {
        step: 'launcher',
        search: '',
        showAllGoals: false,
        showAllRuns: false,
        showAllTemplates: false,
        runItems: [],
        templates: [],
        scopeCounts: {},
        error: '',
        buttonDisabled: false,

        goal: null,
        controlKind: 'control',
        projectScope: '',
        projectAssessment: null,
        title: '',
        titleTouched: false,
        description: '',
        repeats: true,
        scheduleOption: 'year',
        customEvery: 1,
        customInterval: 'Years',
        firstRun: null,
        autoCreate: false,
        showMore: false,
        users: [],
        selectedUsers: [],
        tags: [],
        selectedTags: [],
        newTags: [],
        disabledDates: {
          to: new Date()
        },

        selectedRun: null,
        // Opened for one recurring project only, so there is no way back to the launcher
        runOnly: false,
        runTitleTouched: false,
        runForm: {
          title: '',
          start_date: null,
          due_date: null,
          schedule_message: '',
          include_attachments: 1,
          links: []
        },
        runDuePreset: '2w',
        disabledRunDates: {
          to: new Date(Date.now() - 8640000)
        },
        selectedFiles: [],
        showAddFiles: false,
        inputLink: '',
        inputLinkName: '',
        dateFormat: 'd MMM yyyy'
      }
    },
    props: {
      type: {},
      types: {},
      scope: {}
    },
    components: {
      UserDropdownOption,
      Avatar,
      Datepicker
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getUser() {
        return this.$store.state.user
      },
      projectType() {
        if (!this.goal) {
          return ''
        }
        return this.goal.key === 'controls' ? this.controlKind : this.goal.projectType
      },
      // One time control assessment: an engagement, where the creator becomes Manager and invites the team
      isNewEngagement() {
        return this.projectType === 'control_assessment' && !this.repeats;
      },
      isScheduled() {
        return this.repeats && this.scheduleOption !== 'none';
      },
      // Goals the client's modules allow, like the type list before US 9396
      moduleGoals() {
        let self = this;
        return _.filter(GOALS, function(goal) {
          let client = self.getClient || {};
          if (goal.family === 'assessment' && !client.assessmentModule) {
            return false;
          }
          if (goal.key === 'update' && !client.processingActivitiesModule) {
            return false;
          }
          if (goal.key === 'validation' && !client.processingActivitiesModule) {
            return false;
          }
          if (goal.family === 'control' && !client.riskModule) {
            return false;
          }
          return self.hasGoalAssets(goal);
        });
      },
      // The list pages pass their project family; they show only those goals until "Show all goals"
      familyGoals() {
        let self = this;
        return _.filter(GOALS, function(goal) {
          return goal.family === self.type && self.hasGoalAssets(goal);
        });
      },
      visibleGoals() {
        if (this.type && !this.showAllGoals) {
          return this.familyGoals;
        }
        return this.type ? _.unionBy(this.familyGoals, this.moduleGoals, 'key') : this.moduleGoals;
      },
      hasMoreGoals() {
        return _.unionBy(this.familyGoals, this.moduleGoals, 'key').length > this.familyGoals.length;
      },
      filteredGoals() {
        let self = this;
        let query = this.search.trim().toLowerCase();
        if (!query) {
          return this.visibleGoals;
        }
        return _.filter(this.visibleGoals, function(goal) {
          let text = [
            self.$t('projects.launcher.goals.' + goal.key + '.title'),
            self.$t('projects.launcher.goals.' + goal.key + '.description')
          ].concat(self.goalChips(goal)).join(' ').toLowerCase();
          return text.indexOf(query) !== -1;
        });
      },
      filteredTemplates() {
        let self = this;
        let query = this.search.trim().toLowerCase();
        let goalTypes = _.map(_.filter(this.visibleGoals, 'templateType'), 'templateType');
        return _.filter(this.templates, function(template) {
          return template.name && goalTypes.indexOf(template.type) !== -1 && template.name.toLowerCase().indexOf(query) !== -1;
        }).slice(0, 6);
      },
      filteredRunItems() {
        let query = this.search.trim().toLowerCase();
        if (!query) {
          return this.runItems;
        }
        return _.filter(this.runItems, function(item) {
          return item.title && item.title.toLowerCase().indexOf(query) !== -1;
        });
      },
      visibleRunItems() {
        return this.showAllRuns ? this.filteredRunItems : this.filteredRunItems.slice(0, 3);
      },
      highlightedGoal() {
        return this.filteredGoals.length ? this.filteredGoals[0].key : '';
      },
      canGoBack() {
        return !(this.visibleGoals.length === 1 && !this.runItems.length) && !PAGE_SCOPES[this.scope];
      },
      goalTemplates() {
        let self = this;
        if (!this.goal || !this.goal.templateType) {
          return [];
        }
        return _.filter(this.templates, function(template) {
          return template.type === self.goal.templateType;
        });
      },
      needsScope() {
        return ['update', 'validation', 'control'].indexOf(this.projectType) !== -1;
      },
      // Same module checks as the scope list before US 9396
      scopeOptions() {
        let client = this.getClient || {};
        let options = [];
        if (client.processingActivitiesModule && (this.projectType !== 'validation' || client.gdprAdvancedModule)) {
          options.push({value: 'process'});
          options.push({value: 'activity'});
        }
        if (client.systemModule) {
          options.push({value: 'system'});
        }
        if (client.contractBasicModule) {
          options.push({value: 'third_party'});
        }
        if (client.policyModule) {
          options.push({value: 'policy'});
        }
        return options;
      },
      scheduleOptions() {
        let self = this;
        return [
          {value: 'none', text: this.$t('projects.launcher.schedule.none')},
          {value: 'month', text: this.$t('projects.launcher.schedule.month')},
          {value: 'quarter', text: this.$t('projects.launcher.schedule.quarter')},
          {value: 'half_year', text: this.$t('projects.launcher.schedule.half_year')},
          {value: 'year', text: this.$t('projects.launcher.schedule.year')},
          {value: 'custom', text: self.$t('projects.launcher.schedule.custom')}
        ];
      },
      intervalOptions() {
        return [
          {value: 'Days', text: this.$t('system.interval_days')},
          {value: 'Months', text: this.$t('system.interval_months')},
          {value: 'Years', text: this.$t('system.interval_years')}
        ];
      },
      repeatSchedule() {
        if (!this.isScheduled) {
          return null;
        }
        if (this.scheduleOption === 'custom') {
          return {every: parseInt(this.customEvery, 10), interval: this.customInterval};
        }
        return SCHEDULES[this.scheduleOption];
      },
      scheduleSummary() {
        if (!this.repeats) {
          return this.$t('projects.launcher.once');
        }
        if (!this.isScheduled) {
          return this.$t('projects.launcher.repeats') + ' · ' + this.$t('projects.launcher.schedule.none');
        }
        if (this.scheduleOption === 'custom') {
          return this.$t('projects.launcher.every') + ' ' + this.customEvery + ' ' + _.find(this.intervalOptions, {value: this.customInterval}).text;
        }
        return _.find(this.scheduleOptions, {value: this.scheduleOption}).text;
      },
      firstRunText() {
        var moment = require('moment');
        return this.firstRun ? moment(this.firstRun).format('D MMM YYYY') : '';
      },
      nextSteps() {
        let steps = [];
        if (!this.repeats) {
          steps.push({title: this.$t('projects.launcher.next.created_once_title'), text: this.isNewEngagement ? this.$t('projects.launcher.next.engagement_text') : this.$t('projects.launcher.next.created_once_text')});
          steps.push({title: this.$t('projects.launcher.next.send_title'), text: this.$t('projects.launcher.next.send_text')});
          return steps;
        }
        steps.push({title: this.$t('projects.launcher.next.created_recurring_title'), text: this.$t('projects.launcher.next.created_recurring_text')});
        if (!this.isScheduled) {
          steps.push({title: this.$t('projects.launcher.next.run_manual_title'), text: this.$t('projects.launcher.next.run_manual_text')});
        } else if (this.autoCreate) {
          steps.push({title: this.$t('projects.launcher.next.run_auto_title', {date: this.firstRunText}), text: this.$t('projects.launcher.next.run_auto_text')});
        } else {
          steps.push({title: this.$t('projects.launcher.next.run_due_title', {date: this.firstRunText}), text: this.$t('projects.launcher.next.run_due_text')});
        }
        return steps;
      },
      runIsAssessment() {
        return !!this.selectedRun && ['control_assessment', 'risk_assessment', 'bia_assessment'].indexOf(this.selectedRun.type) !== -1;
      },
      disabledRunEndDates() {
        return {to: this.runForm.start_date ? new Date(this.runForm.start_date) : new Date(Date.now() - 8640000)};
      }
    },
    watch: {
      projectAssessment: function() {
        this.suggestTitle();
      },
      projectScope: function() {
        this.suggestTitle();
      },
      repeats: function() {
        this.suggestTitle();
      }
    },
    methods: {
      showCreateProjectModal() {
        if (!this.getClient || !this.getClient.slug) {
          return;
        }
        this.resetLauncher();
        this.loadRunItems();
        this.loadTemplates();
        this.loadScopeCounts();
        this.$refs['project-launcher'].show();

        // The Processes and Systems pages create update projects on their own assets; go straight to that setup
        if (PAGE_SCOPES[this.scope]) {
          let goal = _.find(GOALS, {key: 'update'});
          this.selectGoal(goal);
          this.selectScope(PAGE_SCOPES[this.scope]);
        }
      },
      // Opens the dialog on one goal, e.g. "risk" from a process's Assessments tab (US 9369 + 9371)
      showGoal(key) {
        if (!this.getClient || !this.getClient.slug) {
          return;
        }
        this.resetLauncher();
        this.loadRunItems();
        this.loadTemplates();
        this.loadScopeCounts();
        this.$refs['project-launcher'].show();
        let goal = _.find(GOALS, {key: key});
        if (goal) {
          this.selectGoal(goal);
        }
      },
      // Opens the Run again sheet for one recurring project, e.g. from the Project management page
      showRunModal(project) {
        if (!this.getClient || !this.getClient.slug) {
          return;
        }
        this.resetLauncher();
        this.runOnly = true;
        this.openRun(project);
        this.$refs['project-launcher'].show();
      },
      hideLauncher() {
        this.$refs['project-launcher'].hide();
      },
      resetLauncher() {
        this.step = 'launcher';
        this.search = '';
        this.showAllGoals = false;
        this.showAllRuns = false;
        this.showAllTemplates = false;
        this.error = '';
        this.buttonDisabled = false;
        this.resetSetup();
        this.selectedRun = null;
        this.runOnly = false;
      },
      resetSetup() {
        this.goal = null;
        this.controlKind = 'control';
        this.projectScope = '';
        this.projectAssessment = null;
        this.title = '';
        this.titleTouched = false;
        this.description = '';
        this.repeats = true;
        this.scheduleOption = 'year';
        this.customEvery = 1;
        this.customInterval = 'Years';
        this.firstRun = this.tomorrow();
        this.autoCreate = false;
        this.showMore = false;
        this.selectedTags = [];
        this.newTags = [];
        this.selectedUsers = this.getUser && this.getUser.id ? [this.getUser] : [];
      },
      backToLauncher() {
        this.error = '';
        this.step = 'launcher';
      },
      tomorrow() {
        var moment = require('moment');
        return moment().add(1, 'day').startOf('day').toDate();
      },
      // A goal is only offered when the client has the modules that give it something to work on
      hasGoalAssets(goal) {
        let client = this.getClient || {};
        if (goal.key === 'validation') {
          return !!client.gdprAdvancedModule;
        }
        if (goal.key === 'risk') {
          return !!(client.processingActivitiesModule || client.systemModule || client.contractBasicModule);
        }
        if (goal.key === 'bia') {
          return !!(client.processingActivitiesModule || client.systemModule);
        }
        return true;
      },
      goalChips(goal) {
        let self = this;
        if (goal.templateType) {
          return _.map(_.filter(this.templates, {type: goal.templateType}).slice(0, 3), 'name');
        }
        return _.map(goal.chips || [], function(chip) {
          return self.$t('projects.launcher.chips.' + chip);
        });
      },
      goalByType(projectType) {
        if (projectType === 'security_measure') {
          return _.find(GOALS, {key: 'controls'});
        }
        return _.find(GOALS, {projectType: projectType}) || GOALS[3];
      },
      goalByTemplateType(templateType) {
        return _.find(GOALS, {templateType: templateType}) || GOALS[0];
      },
      goalIcon(projectType) {
        return projectType === 'security_measure' ? 'lock' : this.goalByType(projectType).icon;
      },
      goalColor(projectType) {
        return this.goalByType(projectType).color;
      },
      runMeta(item) {
        if (item.run_due) {
          return this.$t('projects.launcher.due_now');
        }
        if (item.is_active_child_project) {
          return this.$t('projects.launcher.running_now');
        }
        let nextRun = this.nextRunDate(item);
        if (nextRun) {
          return this.$t('projects.launcher.next_run', {date: nextRun.format('D MMM YYYY')});
        }
        return this.$t('projects.launcher.schedule.none');
      },
      // The project list leaves out nextRun, so it's worked out from the schedule fields
      nextRunDate(item) {
        var moment = require('moment');
        let units = {Days: 'days', Months: 'months', Years: 'years'};
        let every = parseInt(item.repeat_after, 10);
        if (!item.scheduled || !item.start_date || !(every > 0) || !units[item.repeat_after_interval]) {
          return null;
        }
        let start = moment(item.start_date.substring(0, 10));
        let today = moment().startOf('day');
        let next = start.clone();
        for (let i = 1; next.isBefore(today) && i < 10000; i++) {
          next = start.clone().add(every * i, units[item.repeat_after_interval]);
        }
        return next;
      },
      selectGoal(goal) {
        this.resetSetup();
        this.error = '';
        this.goal = goal;
        this.loadUsers();
        this.loadTags();
        if (this.scopeOptions.length === 1) {
          this.projectScope = this.scopeOptions[0].value;
        }
        if (this.goalTemplates.length === 1) {
          this.selectTemplate(this.goalTemplates[0]);
        }
        this.suggestTitle();
        this.step = 'setup';
      },
      selectTemplateResult(template) {
        this.selectGoal(this.goalByTemplateType(template.type));
        this.selectTemplate(template);
      },
      selectTemplate(template) {
        this.projectAssessment = template;
      },
      selectScope(value) {
        this.projectScope = value;
      },
      setControlKind(kind) {
        this.controlKind = kind;
        this.suggestTitle();
      },
      stepCustomEvery(step) {
        let value = (parseInt(this.customEvery, 10) || 0) + step;
        this.customEvery = value < 1 ? 1 : value;
      },
      setRepeats(value) {
        this.repeats = value;
      },
      // A name the user hasn't typed over follows the template or scope they pick
      suggestTitle() {
        if (this.titleTouched || !this.goal) {
          return;
        }
        var moment = require('moment');
        let name = '';
        if (this.goal.templateType) {
          name = this.projectAssessment && this.projectAssessment.name ? this.projectAssessment.name : '';
        } else if (this.projectType === 'security_measure') {
          name = this.$t('projects.launcher.names.security_measure');
        } else if (this.projectScope) {
          name = this.$t('projects.launcher.names.' + this.projectType, {scope: this.$t('projects.launcher.scopes.' + this.projectScope)});
        }
        if (name && !this.repeats) {
          name = name + ' – ' + moment().format('YYYY');
        }
        this.title = name;
      },
      loadRunItems() {
        let self = this;
        piincHttp.get('projects', { params:
          {
            per_page: 100,
            control_type: 'continuous',
            client: this.getClient.slug,
            types: this.types
          }
        }).then(function(response) {
          // Projects that are due first, then the next ones to run
          self.runItems = _.sortBy(response.data.items, function(item) {
            if (item.run_due) {
              return 0;
            }
            return self.nextRunDate(item) ? 1 : 2;
          });
          if (self.step === 'launcher' && self.visibleGoals.length === 1 && !self.runItems.length) {
            self.selectGoal(self.visibleGoals[0]);
          }
        }, function() {});
      },
      loadTemplates() {
        let self = this;
        if (!this.getClient.assessmentModule && this.type !== 'assessment') {
          this.templates = [];
          return;
        }
        piincHttp.get('assessments', { params:
          {
            per_page: 100,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.templates = response.data.items || [];
          if (self.goal && self.goalTemplates.length === 1 && !self.projectAssessment) {
            self.selectTemplate(self.goalTemplates[0]);
          }
        }, function() {});
      },
      loadScopeCounts() {
        let self = this;
        let endpoints = {process: 'processes', activity: 'activities', system: 'systems', third_party: 'third-parties', policy: 'policies'};
        _.forEach(endpoints, function(endpoint, scope) {
          piincHttp.get(endpoint, { params: { per_page: 1, client: self.getClient.slug } }).then(function(response) {
            if (response.data && response.data.total_count !== undefined) {
              self.$set(self.scopeCounts, scope, response.data.total_count);
            }
          }, function() {});
        });
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.tags = response.data
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      validateSetup() {
        let errors = [];
        if (this.goal.templateType && !(this.projectAssessment && this.projectAssessment.id)) {
          errors.push(this.$t('system.assessment_error_message'));
        }
        if (this.needsScope && !this.projectScope) {
          errors.push(this.$t('system.scope_error_message'));
        }
        if (!this.isNewEngagement && !this.selectedUsers.length) {
          errors.push(this.$t('system.users_error_message'));
        }
        if (this.isScheduled && !this.firstRun) {
          errors.push(this.$t('projects.schedule.start_date_error'));
        }
        if (this.isScheduled && !(this.repeatSchedule.every > 0)) {
          errors.push(this.$t('projects.schedule.required_error'));
        }
        return errors.join(' ');
      },
      formSubmit(evt) {
        var moment = require('moment');
        let self = this;
        evt.preventDefault();

        this.error = this.validateSetup();
        if (this.error) {
          return;
        }

        this.buttonDisabled = true;
        let formData = new FormData();

        formData.append("type", this.projectType);
        formData.append("control_type", this.repeats ? 'continuous' : 'one time');
        formData.append("title", this.title);
        formData.append("description", this.description);
        formData.append("client", this.getClient.slug);

        if (this.needsScope) {
          formData.append("scope", this.projectScope);
        }

        if (this.goal.templateType) {
          formData.append("assessment", this.projectAssessment.id);
        }

        // The creator of an engagement becomes its Manager
        if (!this.isNewEngagement) {
          for (var i = 0; i < this.selectedUsers.length; i++) {
            formData.append("users[]", this.selectedUsers[i].id);
          }
        }

        for (var j = 0; j < this.selectedTags.length; j++) {
          formData.append("tags[]", this.selectedTags[j].name_translated);
        }

        // A schedule from the dialog gets the same defaults as the schedule tab needs to run (due after 14 days)
        if (this.repeats) {
          formData.append("scheduled", this.isScheduled ? 1 : 0);
          formData.append("auto_create", this.isScheduled && this.autoCreate ? 1 : 0);
          if (this.isScheduled) {
            formData.append("start_date", moment(this.firstRun).format('YYYY-MM-DD'));
            formData.append("repeat_after", this.repeatSchedule.every);
            formData.append("repeat_after_interval", this.repeatSchedule.interval);
            formData.append("due_after", 14);
            formData.append("due_after_interval", 'Days');
            if (this.projectType === 'control_assessment') {
              formData.append("data_collection_due_after", 14);
              formData.append("data_collection_due_after_interval", 'Days');
            }
          }
        }

        piincHttp.post('/projects', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('projects.create.success'))
          self.buttonDisabled = false;
          self.hideLauncher();
          self.$router.push({ name: 'projects.view', params: { slug: response.data.slug }})
        }, function(error) {
          self.buttonDisabled = false;
          self.error = error.data && error.data.message ? error.data.message : self.$t('projects.launcher.error_generic');
        })
      },
      openRun(item) {
        var moment = require('moment');
        this.error = '';
        this.selectedRun = item;
        this.runForm.start_date = moment().startOf('day').toDate();
        this.runTitleTouched = false;
        this.suggestRunTitle();
        this.runForm.schedule_message = item.schedule_message ? item.schedule_message : '';
        this.runForm.include_attachments = 1;
        this.runForm.links = [];
        this.selectedFiles = [];
        this.showAddFiles = false;
        this.setRunDue('2w');
        this.step = 'run';
      },
      // A name the user hasn't typed over follows the start date
      suggestRunTitle(startDate) {
        var moment = require('moment');
        if (!this.runTitleTouched && this.selectedRun) {
          this.runForm.title = this.selectedRun.title + ' ' + moment(startDate || this.runForm.start_date).format('DD MMM YYYY');
        }
      },
      setRunDue(preset, startDate) {
        var moment = require('moment');
        let start = moment(startDate || this.runForm.start_date);
        if (startDate) {
          this.suggestRunTitle(startDate);
        }
        this.runDuePreset = preset;
        if (preset === '2w') {
          this.runForm.due_date = start.clone().add(14, 'day').toDate();
        } else if (preset === '1m') {
          this.runForm.due_date = start.clone().add(1, 'month').toDate();
        } else if (this.runForm.due_date && moment(this.runForm.due_date).isBefore(start)) {
          this.runForm.due_date = start.toDate();
        }
      },
      onFileChange() {
        let files = this.$refs.runFiles.files;
        this.selectedFiles = [];
        for (var i = 0; i < files.length; i++) {
          this.selectedFiles.push(files[i])
        }
      },
      handleLink() {
        if (this.inputLink !== '') {
          if (!this.inputLink.startsWith('http')) {
            this.inputLink = 'http://' + this.inputLink
          }
          this.runForm.links.push({url: this.inputLink, name: this.inputLinkName})
          this.inputLink = ''
          this.inputLinkName = ''
        }
      },
      runFormSubmit(evt) {
        var moment = require('moment');
        let self = this;
        evt.preventDefault();

        this.handleLink();

        let startDate = this.runForm.start_date ? moment(this.runForm.start_date).format('YYYY-MM-DD') : '';
        let dueDate = this.runForm.due_date ? moment(this.runForm.due_date).format('YYYY-MM-DD') : '';

        if (!startDate || !dueDate || startDate > dueDate) {
          this.error = this.$t('projects.schedule.start_date_greater_error');
          return;
        }

        let formData = new FormData();
        let sizeError = false;
        for (var i = 0; i < this.selectedFiles.length; i++) {
          // Equal to 10 MB
          if (this.selectedFiles[i].size > 10485760) {
            sizeError = true;
          }
          formData.append("files[]", this.selectedFiles[i]);
        }
        if (sizeError) {
          this.error = this.$t('system.file_size');
          return;
        }

        for (var j = 0; j < this.runForm.links.length; j++) {
          formData.append("links[]", JSON.stringify(this.runForm.links[j]));
        }

        formData.append("title", this.runForm.title);
        formData.append("compliance_activity", this.selectedRun.slug);
        formData.append("start_date", startDate);
        formData.append("due_date", dueDate);
        formData.append("include_attachments", this.runForm.include_attachments);
        if (!this.runIsAssessment) {
          formData.append("schedule_message", this.runForm.schedule_message || '');
        }

        this.buttonDisabled = true;
        piincHttp.post('projects/' + this.selectedRun.slug + '/projects', formData).then(function(response) {
          self.buttonDisabled = false;
          self.hideLauncher();
          self.$emit('reloadContent', response.data);
          self.$toastr('success', self.$t('projects.create.project_success'))
        }, function(error) {
          self.buttonDisabled = false;
          self.error = error.data && error.data.message ? error.data.message : self.$t('projects.launcher.error_generic');
          if (error.data && error.data.errors) {
            if (error.data.errors.start_date) {
              self.error += ' ' + error.data.errors.start_date[0]
            }
            if (error.data.errors.due_date) {
              self.error += ' ' + error.data.errors.due_date[0]
            }
          }
        })
      }
    }
  }
</script>
